/**
 * Typing a score, shared by the two numeric steppers that exist for it.
 *
 * The experience default's `ScoringStepper` and the task card's `ScoringMini` are near-identical
 * and were written separately, so each had its own `parseInt` and its own `Math.max(min, n)`. Both
 * therefore clamped BELOW and not above, which is how a 4349203-point penalty became authorable:
 * the bug was not in either of them, it was in there being two of them.
 *
 * Bounds and rounding come from `@agavi/blocks`, so the editor cannot disagree with the scorer.
 */
import { MAX_POINTS, roundScore, SCORE_PRECISION } from '@agavi/blocks';

/** Decimal places a score may carry, derived so it cannot drift from the package's precision. */
export const SCORE_DECIMALS = Math.round(Math.log10(SCORE_PRECISION));

/**
 * What a teacher is allowed to have typed SO FAR.
 *
 * Deliberately permissive about incomplete input — `2.` and `` are both states you pass through on
 * the way to a valid number, and a field that refuses them fights the person using it. Only
 * `commitScore` decides what the value actually becomes.
 *
 * Accepts a comma as the decimal mark, because pt-BR keyboards produce one and a teacher typing
 * `2,5` means two and a half, not twenty-five.
 */
export function sanitizeScoreDraft(raw: string): string {
  const unified = raw.replace(',', '.');
  // First dot survives, later ones do not — `2..5` and `2.5.1` are typos, not new numbers.
  const [whole = '', ...rest] = unified.replace(/[^0-9.]/g, '').split('.');
  if (rest.length === 0) return whole;
  return `${whole}.${rest.join('').slice(0, SCORE_DECIMALS)}`;
}

/**
 * The value a draft becomes on blur: rounded to 2dp and held inside `[min, max]`.
 *
 * `fallback` is returned for anything unreadable, so clearing the field and tabbing away restores
 * what was there rather than silently zeroing a task's points.
 */
export function commitScore(
  draft: string,
  fallback: number,
  { min = 0, max = MAX_POINTS }: { min?: number; max?: number } = {},
): number {
  const parsed = Number.parseFloat(draft);
  if (!Number.isFinite(parsed)) return fallback;
  // Rounded BEFORE clamping, so a max of 5 cannot be beaten by 5.004.
  return Math.min(max, Math.max(min, roundScore(parsed)));
}

/**
 * How a score is written in the UI: trailing zeros dropped.
 *
 * `2.50` is the same number as `2.5` and the shorter one is what a teacher typed; carrying the
 * zero through makes every integer read as `5.00` and the panel harder to scan.
 */
export function formatScore(value: number): string {
  return String(roundScore(value));
}
